import React from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  SxProps,
} from '@mui/material';
import SearchDropDown from '../../../common/SearchDropDown';
import ErrorBadge from '../../../common/ErrorBadge';
import { NavigationGroup } from '../types';
import { PropertyField } from '../../../../system/field';
import SetFieldDefaults from '../SetFieldDefaults';
import { FeatureFlags } from '../../../../flags';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { EditNavigationIcons } from '../EditNavigationIcons';
import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from '@mui/icons-material';
import { PreferenceKey, usePreferences } from 'src/hooks/usePreferences';
import { useTrackScrollPosition } from '../hooks/useTrackScrollPosition';

interface DesktopNavigationProps {
  groups: NavigationGroup[];
  activeGroup: string;
  activeSection: string;
  navHeight: string;
  sectionErrors: { [key: string]: number };
  groupErrors: { [key: string]: number };
  setDefaultsStyles: SxProps;
  handleSearchEntriesChange: (fields: PropertyField[] | null) => void;
  handleGroupClick: (
    groupName: string,
    fieldName?: string,
    isMobile?: boolean,
    followError?: boolean,
  ) => void;
  handleSectionClick: (sectionName: string, followError?: boolean) => void;
}

const ResponsiveDesktopNavigation: React.FC<DesktopNavigationProps> = ({
  groups,
  activeGroup,
  activeSection,
  navHeight,
  sectionErrors,
  groupErrors,
  setDefaultsStyles,
  handleGroupClick,
  handleSectionClick,
  handleSearchEntriesChange,
}) => {
  const { hasFeature } = useFeatureFlags();
  const hasSetFieldDefaults = hasFeature(FeatureFlags.NGW_EDIT_MEMBER_DEFAULTS);
  const { usePreference } = usePreferences();
  // Use the preferences hook for AI descriptions enabled state
  const [menuIsExpanded, setMenuIsExpanded] = usePreference<boolean>(
    PreferenceKey.EDIT_MENU_IS_EXPANDED,
    true, // default value: enabled
  );

  const scrollPosition = useTrackScrollPosition();

  return (
    <Box
      data-testid="edit-navigation"
      sx={{
        width: menuIsExpanded ? '296px' : '64px',
        bgcolor: 'background.paper',
        position: 'sticky',
        top: 125,
        alignSelf: 'flex-start',
        bottom: 0,
        borderRight: '1px solid',
        borderColor: 'grey.300',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        paddingBottom: '80px', // prevents SFD button from covering bottom of sections list
        transitionProperty: 'height',
        transitionDuration: '50ms',
        transitionTimingFunction: 'ease',
        height: `calc(${navHeight} - ${scrollPosition <= 125 ? 125 - scrollPosition : 0}px)`, // 125 offsets to top position
      }}
    >
      <Box
        sx={{
          px: menuIsExpanded ? 2 : 0,
          py: 3,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: menuIsExpanded ? 'flex-start' : 'center',
        }}
      >
        {menuIsExpanded ? (
          <KeyboardDoubleArrowLeft
            fill="secondary.medium"
            onClick={() => setMenuIsExpanded(false)}
          />
        ) : (
          <KeyboardDoubleArrowRight
            fill="secondary.medium"
            onClick={() => setMenuIsExpanded(true)}
          />
        )}
        <Typography
          sx={{
            color: 'secondary.medium',
            fontSize: '20px',
            marginLeft: '10px',
            fontWeight: '700',
            lineHeight: '30px',
          }}
        >
          {menuIsExpanded ? 'Listing Sections' : ''}
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 1,
        }}
        onClick={() => {
          if (!menuIsExpanded) {
            setMenuIsExpanded(true);
          }
        }}
      >
        <SearchDropDown
          menuIsExpanded={menuIsExpanded}
          data={groups}
          onResultsChange={handleSearchEntriesChange}
          onClick={(groupName, fieldLabel) => {
            handleGroupClick(groupName, fieldLabel, false, false);
          }}
        />
      </Box>
      <Divider
        sx={{
          marginTop: '25px',
          borderWidth: '1px',
          width: '80%',
          margin: '0 auto',
        }}
      />
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'scroll',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
        }}
        data-testid="body-content"
        data-nav-sections
      >
        <List data-testid="desktop-nav">
          {groups.map((group) => (
            <React.Fragment key={group.name}>
              <ListItem
                disablePadding
                sx={{
                  width: menuIsExpanded ? '100%' : '64px',
                }}
              >
                <ListItemButton
                  data-group-id={group.name}
                  selected={activeGroup === group.name}
                  onClick={() =>
                    handleGroupClick(group.name, undefined, false, false)
                  }
                  sx={{
                    marginRight: menuIsExpanded ? '' : '0px',
                    color: 'secondary.medium',
                    marginTop: '11px',
                    lineHeight: '24px',
                    paddingLeft: '16px',
                    borderRadius: '8px',
                    margin: menuIsExpanded ? '' : '4px 8px',
                    padding: menuIsExpanded ? '' : '10px 10px',
                    backgroundColor: 'transparent!important',
                    paddingRight: '16px',
                    '&:hover': {
                      backgroundColor: menuIsExpanded
                        ? '#e8edf6!important'
                        : '',
                    },
                  }}
                >
                  <EditNavigationIcons groupName={group.name} />
                  <ListItemText
                    primary={group.name}
                    id="desktop-nav-group-name"
                    sx={{
                      display: menuIsExpanded ? 'block' : 'none',
                      pl: 1,
                      fontWeight: '400',
                      borderRadius: '8px',
                      color: 'secondary.medium',
                      lineHeight: '20px',
                      fontSize: '0.9rem!important',
                      '& span': {
                        fontSize: '0.9rem!important',
                      },
                    }}
                  />

                  {groupErrors[group.name] > 0 &&
                    activeGroup !== group.name && (
                      <ErrorBadge
                        collapsed={menuIsExpanded === false}
                        count={groupErrors[group.name]}
                        onClick={() =>
                          handleGroupClick(group.name, undefined, false, true)
                        }
                      />
                    )}
                  {groupErrors[group.name] > 0 && menuIsExpanded !== true && (
                    <ErrorBadge
                      collapsed={menuIsExpanded === false}
                      count={groupErrors[group.name]}
                      onClick={() =>
                        handleGroupClick(group.name, undefined, false, true)
                      }
                    />
                  )}
                </ListItemButton>
              </ListItem>
              {activeGroup === group.name && (
                <Box
                  sx={{
                    marginTop: menuIsExpanded ? '0px' : '8px',
                    marginRight: '16px',
                  }}
                >
                  {group.sections.map((section) => (
                    <Box
                      key={section.name}
                      data-section-id={section.name}
                      onClick={() => handleSectionClick(section.name, false)}
                      sx={{
                        display: menuIsExpanded ? 'flex' : 'none',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        scrollMargin: '20px',
                      }}
                    >
                      <Typography
                        sx={{
                          backgroundColor:
                            activeSection === section.name
                              ? 'background.secondary'
                              : 'transparent',
                          '&:hover': {
                            backgroundColor: 'background.secondary',
                          },
                          borderTopRightRadius: '8px',
                          borderBottomRightRadius: '8px',
                          pt: '10px',
                          height: '40px',
                          color: 'secondary.medium',
                          fontSize: '14px',
                          fontWeight: '400',
                          marginRight: '16px',
                          maxWidth: '250px',
                          paddingLeft: '40px',
                          flexGrow: 1,
                          textAlign: 'left',
                          width: '225px',
                        }}
                      >
                        {menuIsExpanded ? section.name : ''}
                      </Typography>
                      {sectionErrors[section.name] > 0 && (
                        <ErrorBadge
                          collapsed={menuIsExpanded === false}
                          count={sectionErrors[section.name]}
                          onClick={() => handleSectionClick(section.name, true)}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {hasSetFieldDefaults && (
        <SetFieldDefaults
          outlinedIcon={true}
          content={
            <Typography>
              {menuIsExpanded ? 'Set Field Defaults' : ''}
            </Typography>
          }
          isMobile={false}
          styles={setDefaultsStyles}
        />
      )}
    </Box>
  );
};

export default ResponsiveDesktopNavigation;
